/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Legacy Adapter
 * ============================================================================
 * 
 * Adaptador de compatibilidade para integrar o VCCE com o código existente.
 * 
 * Este módulo fornece uma interface que imita a API do voiceProcessor.js antigo,
 * permitindo migração gradual sem quebrar o ChatRoom.js existente.
 * 
 * ESTRATÉGIA DE MIGRAÇÃO:
 * 1. ChatRoom.js usa este adaptador em vez do voiceProcessor.js
 * 2. O adaptador traduz chamadas para o novo VCCE
 * 3. Após validação, o adaptador pode ser removido e ChatRoom refatorado
 * 
 * @author Vocalira Team
 * @version 2.0.1 - VCCE Architecture (Gate Fixes)
 */

import VCCEPipeline, { ConversationState } from './VCCEPipeline.js';

// ============================================================================
// MAPEAMENTO DE ESTADOS (Legacy -> VCCE)
// ============================================================================

/**
 * Estados do voiceProcessor.js antigo (para compatibilidade).
 */
export const VoiceState = {
  IDLE: 'idle',
  LISTENING: 'listening',
  DETECTING_PAUSE: 'detecting_pause',
  PROCESSING: 'processing',
  INTERPRETING: 'interpreting',
  SPEAKING: 'speaking',
  WAITING_TO_LISTEN: 'waiting',
  ERROR: 'error'
};

/**
 * Labels em português (compatibilidade).
 */
export const VoiceStateLabels = {
  idle: '',
  listening: 'Sua vez de falar...',
  detecting_pause: 'Aguardando...',
  processing: 'Processando...',
  interpreting: 'Interpretando...',
  speaking: 'Lira está falando...',
  waiting: 'Preparando...',
  stillListening: 'Ainda ouvindo...',
  error: 'Erro'
};

/**
 * VAD Sensitivity Presets (compatibilidade).
 */
export const VADSensitivity = {
  FAST: 'fast',
  NORMAL: 'normal',
  PATIENT: 'patient',
};

/**
 * VAD Sensitivity Labels (Portuguese).
 */
export const VADSensitivityLabels = {
  fast: 'Rápido (adaptativo)',
  normal: 'Normal (adaptativo)',
  patient: 'Paciente (adaptativo)',
};

/**
 * VAD Sensitivity Descriptions.
 */
export const VADSensitivityDescriptions = {
  fast: 'Respostas rápidas - adapta-se ao ambiente',
  normal: 'Equilibrado - adapta-se ao seu ritmo',
  patient: 'Mais tempo para pensar - adapta-se à situação',
};

/**
 * Mapeia estados do VCCE para estados legacy.
 */
const VCCE_TO_LEGACY_STATE = {
  [ConversationState.IDLE]: VoiceState.IDLE,
  [ConversationState.READY]: VoiceState.IDLE,
  [ConversationState.LISTENING]: VoiceState.LISTENING,
  [ConversationState.VOICE_DETECTED]: VoiceState.LISTENING,
  [ConversationState.TRANSCRIBING]: VoiceState.LISTENING,
  [ConversationState.TRANSCRIPT_FINAL]: VoiceState.DETECTING_PAUSE,
  [ConversationState.EVALUATING]: VoiceState.DETECTING_PAUSE,
  [ConversationState.PROCESSING]: VoiceState.PROCESSING,
  [ConversationState.LLM_STREAMING]: VoiceState.INTERPRETING,
  [ConversationState.TTS_GENERATING]: VoiceState.INTERPRETING,
  [ConversationState.SPEAKING]: VoiceState.SPEAKING,
  [ConversationState.INTERRUPTED]: VoiceState.LISTENING,
  [ConversationState.ERROR]: VoiceState.ERROR,
  [ConversationState.WAITING_TO_LISTEN]: VoiceState.WAITING_TO_LISTEN,
};

// ============================================================================
// CLASSE: VCCEAdapter (substitui VoiceProcessor)
// ============================================================================

/**
 * Adaptador que expõe a mesma API do VoiceProcessor antigo,
 * mas usa o VCCE internamente.
 */
class VCCEAdapter {
  constructor(options = {}) {
    // Callbacks no formato antigo
    this.onStateChange = options.onStateChange || (() => {});
    this.onTranscript = options.onTranscript || (() => {});
    this.onInterimTranscript = options.onInterimTranscript || (() => {});
    this.onAutoFinish = options.onAutoFinish || (() => {});
    // Suportar ambos os nomes: onVolumeChange (novo) e onVolumeLevel (legacy)
    this.onVolumeChange = options.onVolumeChange || options.onVolumeLevel || (() => {});
    this.onError = options.onError || (() => {});
    this.onNoiseFloorEstimated = options.onNoiseFloorEstimated || (() => {});
    this.onPauseFeedback = options.onPauseFeedback || (() => {});
    this.onNoiseSuppressed = options.onNoiseSuppressed || (() => {});
    this.onLowConfidence = options.onLowConfidence || (() => {});
    this.onTurnDetected = options.onTurnDetected || (() => {});
    this.onNoiseOnlyDetected = options.onNoiseOnlyDetected || (() => {});
    
    // Estado legacy para compatibilidade
    this.currentState = VoiceState.IDLE;
    this.isProcessing = false;
    this.transcript = '';
    this.interimTranscript = '';
    
    // Propriedades de compatibilidade
    this.intelligentTurnEnabled = true;
    this.conversationContext = [];
    this.sensitivity = 'normal';
    
    // Criar pipeline VCCE
    this.pipeline = new VCCEPipeline({
      debugEnabled: options.debugEnabled || false,
      
      onStateChange: (event) => this._handleStateChange(event),
      onTranscript: (text) => this._handleTranscript(text),
      onInterimTranscript: (text) => this._handleInterimTranscript(text),
      onTurnComplete: (result) => this._handleTurnComplete(result),
      onVolumeChange: (vol, isSpeech) => this._handleVolumeChange(vol, isSpeech),
      onError: (err) => this._handleError(err),
      onDecisionUpdate: (update) => this._handleDecisionUpdate(update),
    });
    
    // Métricas para compatibilidade (estrutura compatível com voiceProcessor.js)
    this.metrics = {
      captureStartTime: null,
      speechDetections: 0,
      pausesCancelled: 0,
      noiseSuppressions: 0,
      recognitionRestarts: 0,
      avgVolume: 0,
      peakVolume: 0,
      transcriptLength: 0,
      finalTranscript: '',
      // VCCE specific
      vcce: null,
    };
    
    console.log('[VCCEAdapter] Initialized with VCCE Pipeline v2.0.1');
  }
  
  // ==========================================================================
  // API PÚBLICA (compatível com VoiceProcessor)
  // ==========================================================================
  
  /**
   * Inicia captura de áudio.
   * SPRINT 3A FIX: Passa idioma diretamente para o pipeline.start()
   * O pipeline agora é a Single Source of Truth para o idioma.
   * 
   * @param {string} language - Idioma para reconhecimento (ex: 'en-US', 'pt-BR')
   * @returns {Promise<boolean>}
   */
  async start(language = 'en-US') {
    console.log('[VCCEAdapter] start() called with language:', language);
    
    // SPRINT 3A: Armazenar idioma no adapter também para referência
    this._currentLanguage = language;
    
    this.metrics.captureStartTime = Date.now();
    this.transcript = '';
    this.interimTranscript = '';
    this.isProcessing = true;
    
    // SPRINT 3A FIX: Passar idioma diretamente para pipeline.start()
    // O pipeline cria o SpeechRecognition com o idioma correto
    const success = await this.pipeline.start(language);
    
    if (success) {
      this.onNoiseFloorEstimated(this.pipeline.noiseFloor);
      console.log('[VCCEAdapter] Pipeline started with language:', language);
    }
    
    return success;
  }
  
  /**
   * Para captura de áudio.
   */
  stop() {
    console.log('[VCCEAdapter] stop() called');
    
    this.pipeline.stop();
    this.isProcessing = false;
    this._setState(VoiceState.IDLE);
  }
  
  /**
   * Finaliza o turno atual.
   * @returns {string} Transcript final
   */
  finishListening() {
    console.log('[VCCEAdapter] finishListening() called');
    
    this.pipeline.forceFinishTurn();
    return this.transcript;
  }
  
  /**
   * Limpa recursos.
   */
  destroy() {
    console.log('[VCCEAdapter] destroy() called');
    this.pipeline.stop();
  }
  
  /**
   * Retorna transcript atual.
   */
  getTranscript() {
    return this.transcript;
  }
  
  /**
   * Retorna estado atual (formato legacy).
   */
  getState() {
    return this.currentState;
  }
  
  /**
   * Define estado de "Lira falando".
   */
  setSpeaking(isSpeaking) {
    if (isSpeaking) {
      this._setState(VoiceState.SPEAKING);
      this.pipeline.stateMachine.speaking();
    } else {
      // Após Lira terminar de falar
      this._setState(VoiceState.IDLE);
      this.pipeline.stateMachine.ready();
    }
  }
  
  /**
   * Alias para setSpeaking (compatibilidade com ChatRoom.js).
   */
  setLiraSpeaking(isSpeaking) {
    this.setSpeaking(isSpeaking);
  }
  
  /**
   * Define estado como IDLE.
   */
  setIdle() {
    console.log('[VCCEAdapter] setIdle() called');
    this._setState(VoiceState.IDLE);
    this.pipeline.stateMachine.idle();
  }
  
  /**
   * Define estado como INTERPRETING.
   */
  setInterpreting() {
    console.log('[VCCEAdapter] setInterpreting() called');
    this._setState(VoiceState.INTERPRETING);
    this.pipeline.stateMachine.llmStreaming();
  }
  
  /**
   * Habilita/desabilita Intelligent Turn Detection.
   * @param {boolean} enabled
   */
  setIntelligentTurnEnabled(enabled) {
    console.log('[VCCEAdapter] setIntelligentTurnEnabled:', enabled);
    this.intelligentTurnEnabled = enabled;
    // VCCE sempre usa intelligent turn detection, então isso é no-op
  }
  
  /**
   * Atualiza contexto da conversa (para análise linguística futura).
   * @param {Array} messages - Array de mensagens da conversa
   */
  updateConversationContext(messages) {
    console.log('[VCCEAdapter] updateConversationContext:', messages?.length, 'messages');
    this.conversationContext = messages || [];
    // Futuro: passar para o pipeline para análise de contexto
  }
  
  /**
   * Prepara para novo turno.
   * SPRINT 1: Agora garante limpeza completa e ciclo de vida isolado.
   */
  prepareForNewTurn() {
    console.log('[VCCEAdapter] prepareForNewTurn() called - SPRINT 1 Isolated Lifecycle');
    
    // Limpar transcrições locais
    this.transcript = '';
    this.interimTranscript = '';
    
    // Resetar métricas para novo turno
    this.metrics.captureStartTime = null;
    this.metrics.speechDetections = 0;
    this.metrics.pausesCancelled = 0;
    this.metrics.noiseSuppressions = 0;
    this.metrics.recognitionRestarts = 0;
    this.metrics.avgVolume = 0;
    this.metrics.peakVolume = 0;
    this.metrics.transcriptLength = 0;
    this.metrics.finalTranscript = '';
    
    // SPRINT 1: Chamar reset do pipeline que agora destrói e recria recognition
    this.pipeline.resetForNewTurn();
    
    console.log('[VCCEAdapter] Turn prepared - transcript cleared, recognition will be recreated on start()');
  }
  
  /**
   * Retorna métricas.
   */
  getMetrics() {
    return {
      ...this.metrics,
      vcce: this.pipeline.getMetrics(),
    };
  }
  
  /**
   * Retorna log de auditoria.
   */
  getAuditLog() {
    return this.pipeline.getAuditLog();
  }
  
  // ==========================================================================
  // CONFIGURAÇÕES (compatibilidade)
  // ==========================================================================
  
  /**
   * Define sensibilidade do VAD.
   * @param {'fast'|'normal'|'patient'} sensitivity
   */
  setSensitivity(sensitivity) {
    // Mapear para tipo de ambiente do VCCE
    const environmentMap = {
      'fast': 'quiet',
      'normal': 'normal', 
      'patient': 'noisy',
    };
    
    this.pipeline.setEnvironmentType(environmentMap[sensitivity] || 'normal');
  }
  
  /**
   * Habilita/desabilita modo contínuo.
   */
  setContinuousMode(enabled) {
    this.pipeline.setContinuousMode(enabled);
  }
  
  /**
   * Define idioma do reconhecimento.
   * SPRINT 3A: Atualiza tanto o adapter quanto o pipeline.
   * Nota: O idioma efetivo é aplicado no próximo start().
   */
  setLanguage(lang) {
    console.log('[VCCEAdapter] setLanguage() called with:', lang);
    this._currentLanguage = lang;
    
    // Se já existe um recognition ativo, atualizar (para mid-session changes)
    if (this.pipeline.recognition) {
      this.pipeline.recognition.lang = lang;
    }
    
    // Atualizar no pipeline para o próximo turno
    if (this.pipeline) {
      this.pipeline._currentLanguage = lang;
    }
  }
  
  // ==========================================================================
  // HANDLERS INTERNOS
  // ==========================================================================
  
  _handleStateChange(event) {
    const legacyState = VCCE_TO_LEGACY_STATE[event.state] || VoiceState.IDLE;
    this._setState(legacyState);
  }
  
  _handleTranscript(text) {
    this.transcript = text;
    this.onTranscript(text);
  }
  
  _handleInterimTranscript(text) {
    this.interimTranscript = text;
    this.onInterimTranscript(text);
  }
  
  _handleTurnComplete(result) {
    console.log('[VCCEAdapter] Turn complete:', result.reason);
    
    this._setState(VoiceState.PROCESSING);
    this.isProcessing = false;
    
    // Atualizar métricas para compatibilidade com voiceProcessor.js
    this.metrics.transcriptLength = result.transcript?.length || 0;
    this.metrics.finalTranscript = result.transcript || '';
    this.metrics.vcce = this.pipeline.getMetrics();
    
    // VCCE INTEGRATION FIX: Usar onTurnDetected como canal primário
    // O ChatRoom.js espera onTurnDetected quando intelligentTurnEnabled=true
    // Isso alinha o contrato de comunicação entre VCCE e ChatRoom
    const turnData = {
      transcript: result.transcript,
      confidence: result.confidence,
      reason: result.reason,
      duration: result.duration,
      isLocal: false,
      shouldClarify: result.confidence < 0.5 && result.transcript?.length < 10,
      clarificationReason: result.confidence < 0.5 ? 'LOW_CONFIDENCE' : null,
    };
    
    // Chamar onTurnDetected (canal principal para intelligent turn)
    this.onTurnDetected(turnData);
  }
  
  _handleVolumeChange(volume, isSpeech) {
    // Atualizar métricas para compatibilidade com voiceProcessor.js
    this.metrics.avgVolume = this.metrics.avgVolume * 0.95 + volume * 0.05;
    this.metrics.peakVolume = Math.max(this.metrics.peakVolume, volume);
    
    if (isSpeech) {
      this.metrics.speechDetections++;
    }
    
    this.onVolumeChange({
      volume,
      isSpeech,
      smoothedVolume: volume,
    });
  }
  
  _handleDecisionUpdate(update) {
    // Mostrar feedback de pausa se score está aumentando
    if (update.score > 0.3 && update.score < update.threshold) {
      this.onPauseFeedback(true, 'Ainda ouvindo...', 
        Math.round((update.threshold - update.score) * 2000));
    } else if (update.score >= update.threshold || update.score < 0.2) {
      this.onPauseFeedback(false, '');
    }
  }
  
  _handleError(error) {
    console.error('[VCCEAdapter] Error:', error);
    this._setState(VoiceState.ERROR);
    this.onError(error);
  }
  
  _setState(state) {
    if (this.currentState !== state) {
      const previousState = this.currentState;
      this.currentState = state;
      const label = VoiceStateLabels[state] || '';
      
      // Passar label como segundo argumento (compatibilidade com ChatRoom.js)
      this.onStateChange(state, label);
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export { VCCEAdapter };
export default VCCEAdapter;

// Alias para compatibilidade direta
export { VCCEAdapter as VoiceProcessor };
